<template>
  <div>
    <student-new-dialog :state="onCreate" @done="refreshAfterCreate" />
    <div class="d-flex justify-space-between align-center pa-4 pa-md-2">
      <div>
        <Breadcrumbs headline="Học viên" :link="[{ text: 'Học viên', href: '../students' }]" />
      </div>

      <div class="d-flex">
        <v-btn
          color="primary"
          @click="onCreate = !onCreate"
          v-if="['super_admin', 'education_manager'].includes(role.type)"
          ><v-icon left>add</v-icon>{{ addButtonText }}</v-btn
        >
        <student-list-actions
          v-if="['super_admin', 'education_manager'].includes(role.type)"
          :selected="selected"
          @removed="selected = []"
          :filter="draw"
          @filter="draw = !draw"
        />
      </div>
    </div>

    <v-card class="pa-2 pa-md-2 mx-md-2">
      <v-row no-gutters>
        <v-col class="text-right pa-0">
          <span v-if="$vuetify.breakpoint.smAndDown">
            <student-filter-dialog @onFilterChanged="refresh" />
          </span>
          <!-- <setting-table-header :default-headers="originHeaders" @change="headers = $event" /> -->
          <v-menu :close-on-content-click="false">
            <template v-slot:activator="{ on }">
              <v-btn text v-on="on" icon class="ml-2">
                <v-icon>mdi-settings</v-icon>
              </v-btn>
            </template>
            <v-list class="px-2" dense style="width: 200px">
              <v-list-item
                v-show="header.configurable"
                dense
                v-for="header in headers"
                :key="header.value"
              >
                <v-checkbox
                  class="mt-0"
                  hide-details="auto"
                  small
                  :label="header.text"
                  :input-value="header.show"
                  @change="onToggleHeader(header.value, !header.show)"
                ></v-checkbox
              ></v-list-item> </v-list
          ></v-menu>
          <span v-if="$vuetify.breakpoint.mdAndUp">
            <drop-menu>
              <export-excel :custom-header="headers" api="/students/" />
            </drop-menu>
          </span>
        </v-col>
      </v-row>
      <student-filter
        ref="studentFilter"
        v-if="$vuetify.breakpoint.mdAndUp"
        @onFilterChanged="refresh"
      />
      <v-data-table
        class="nowrap"
        item-key="id"
        v-model="selected"
        :options.sync="studentTableOptions"
        :server-items-length="totalItems"
        :headers="visibleHeaders"
        :items="students"
        :loading="loading"
        :items-per-page="50"
        :footer-props="{
          itemsPerPageOptions: [10, 15, 20, 30, 50]
        }"
        :show-select="
          ['super_admin', 'education_manager'].includes(role.type) && $vuetify.breakpoint.mdAndUp
        "
        dense
      >
        <template v-slot:[`item.status`]="{ item }">
          <v-chip v-if="item.status" small label dark :color="getColor(item.status)">{{
            item.status | getStatus
          }}</v-chip>
        </template>
        <template v-slot:[`item.index`]="{ item }">
          <span v-if="item.index !== undefined">{{ item.index + 1 }}</span>
        </template>
        <template v-slot:[`item.name`]="{ item }">
          <card-student-name :student="item" link />
        </template>
        <template v-slot:[`item.dob`]="{ item }">
          <span style="white-space: nowrap">{{ item.dob ? formatDate(item.dob) : '' }}</span>
        </template>
        <template v-slot:[`item.generation`]="{ item }">
          <span style="white-space: nowrap">{{ item.generation ? item.generation.code : '' }}</span>
        </template>
        <template v-slot:[`item.tags`]="{ item }">
          <span style="white-space: nowrap">{{ item.tags }}</span>
        </template>
        <template v-slot:[`item.classes`]="{ item }">
          <span>{{ item.classes | getClassCount }}</span>
        </template>
        <template v-slot:[`item.majors`]="{ item }">
          <span style="white-space: nowrap" v-for="(major, index) in item.majors" :key="major.id">
            <router-link :to="'/major/' + major.id">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{ major.code }}</span>
                </template>
                <span>{{ major.title }}</span>
              </v-tooltip>
            </router-link>
            <span v-if="index < item.majors.length - 1">{{ ', ' }}</span>
          </span>
        </template>
        <template v-slot:[`item.gender`]="{ item }">{{
          item.gender === 'male' ? 'Nam' : item.gender === 'female' ? 'Nữ' : 'Khác'
        }}</template>
        <template v-slot:[`item.rootMajor`]="{ item }">{{ getType(item.rootMajor) }}</template>
        <template v-slot:[`item.action`]="{ item }">
          <v-btn text icon @click="onStudentSelected(item)">
            <v-icon dense>mdi-pencil</v-icon>
          </v-btn>
        </template>
        <template v-slot:[`item.tuition`]="{ item }">
          <span style="white-space: nowrap">{{ getTuitionStatus(item.leads) }}</span>
        </template>
        <template
          v-slot:[`item.actions`]="{ item }"
          v-if="['super_admin', 'education_manager'].includes(role.type)"
        >
          <single-student-list-actions @reserve="loadDataOnCreated" :selected="[item]" />
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import StudentListActions from '@/modules/student/StudentListActions'
import SingleStudentListActions from '@/modules/student/SingleStudentListActions'
import StudentFilterDialog from '@/modules/student/StudentFilterDialog'
import StudentNewDialog from '@/modules/student/StudentNewDialog'
import CardStudentName from '@/components/basic/card/CardStudentName.vue'
import StudentFilter from '@/modules/student/StudentFilter'
import DropMenu from '@/modules/student/menu/Menu.vue'
import Breadcrumbs from '@/components/layout/Breadcrumbs.vue'
import SettingTableHeader from '@/components/basic/table/SettingHeaders'
import ExportExcel from '@/components/basic/ExportExcel'
import { get } from 'lodash'
import { formatDate } from '@/helpers'

import moment from 'moment'
export default {
  components: {
    CardStudentName,
    DropMenu,
    SingleStudentListActions,
    StudentListActions,
    StudentNewDialog,
    StudentFilter,
    Breadcrumbs,
    SettingTableHeader,
    StudentFilterDialog,
    ExportExcel
  },
  props: {
    role: String
  },
  data() {
    return {
      headers: [
        { text: 'STT', value: 'index', align: 'left', sortable: false, show: true },
        { text: 'Tên', value: 'name', align: 'left', sortable: false, show: true },
        // {
        //   text: 'Mã học viên',
        //   value: 'code',
        //   align: 'left',
        //   sortable: false,
        //   show: true
        // },
        {
          text: 'Giới tính',
          value: 'gender',
          align: 'left',
          sortable: false,
          show: true,
          configurable: true
        },
        {
          text: 'Ngày sinh',
          value: 'dob',
          align: 'left',
          sortable: false,
          show: true,
          configurable: true
        },
        {
          text: 'Khóa',
          value: 'generation',
          align: 'left',
          sortable: false,
          show: false
        },
        { text: 'Nhóm', value: 'tags', align: 'left', sortable: false, show: true },
        { text: 'Số Lớp', value: 'classes', align: 'left', sortable: false, show: false },
        {
          text: 'Chuyên ngành',
          value: 'majors',
          align: 'left',
          sortable: false,
          configurable: true,
          show: false
        },
        {
          text: 'Loại',
          value: 'rootMajor',
          align: 'left',
          sortable: false,
          configurable: true,
          show: false
        },
        {
          text: 'Học Phí',
          value: 'tuition',
          align: 'left',
          sortable: false,
          show: false,
          configurable: true
        },
        {
          text: 'Trạng Thái',
          value: 'status',
          align: 'left',
          sortable: false,
          show: true,
          configurable: true
        },
        {
          text: 'Số Điện Thoại',
          value: 'phone',
          align: 'left',
          sortable: false,
          show: true,
          configurable: true
        },
        {
          text: 'Email',
          value: 'email',
          align: 'left',
          sortable: false,
          show: true,
          configurable: true
        },
        {
          text: 'Hành động',
          value: 'actions',
          align: 'left',
          sortable: false,
          show: true
        }
      ],
      selected: [],
      draw: false,
      search: '',
      status: null,
      loading: false,
      range: { from: null, to: null },
      previewUserId: null,
      ready: false,
      studentTableOptions: {},
      onCreate: false,
      selectedStudent: {}
    }
  },
  async created() {
    this.loadDataOnCreated()
  },
  computed: {
    ...mapState('app', ['department']),
    ...mapState('students', ['totalItems', 'students']),
    ...mapState('auth', ['profile', 'role']),
    statusQueryParam() {
      return get(this.$route.query, 'status', 'active')
    },
    typeQueryParam() {
      return get(this.$route.query, 'type', 'long_term')
    },

    visibleHeaders() {
      return this.headers.filter(i => i.show)
    },
    addButtonText() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
        case 'sm':
          return 'Thêm'
        default:
          return 'Thêm học viên'
      }
    }
  },
  methods: {
    ...mapActions('students', ['requestPageSettings', 'searchStudents']),
    formatDate,
    onToggleHeader(key, value) {
      this.headers = this.headers.map(i => {
        if (i.value === key) {
          return { ...i, show: value }
        }
        return i
      })
    },
    updateDraw(draw) {
      this.draw = draw
    },
    getColor(status) {
      if (status === 'active') return 'green'
      if (status === 'reserved') return 'orange'
      else return 'gray'
    },
    getType(type) {
      return type ? type.title : ''
    },
    async refresh(query) {
      this.loading = true
      if (query.year) {
        query.dob_gte = moment(`${query.year}-01-01`, 'YYYY-MM-DD').toISOString()
        query.dob_lte = moment(`${query.year}-12-31`, 'YYYY-MM-DD').toISOString()
        delete query.year
      }

      await this.searchStudents({ department: this.department.id, ...query })
      this.loading = false
    },
    async loadDataOnCreated() {
      const initialType = get(this.$route.query, 'type', 'long_term')
      const initialStatus = get(this.$route.query, 'status', 'active')

      await this.searchStudents({
        _sort: 'createdAt:desc',
        status: initialStatus,
        type: initialType,
        department: this.department.id
      })
    },
    async refreshAfterCreate() {
      this.requestPageSettings({})
      await this.loadDataOnCreated()
    },
    getTuitionStatus(leads) {
      if (!leads) return ''
      return leads
        .map(lead => {
          return lead.liabilities
        })
        .reduce((a, b) => a + b, 0) >= 0
        ? ''
        : 'Nợ'
    }
  },

  watch: {
    studentTableOptions: {
      handler(newOptions, oldOptions) {
        const itemPerPageChanged = newOptions.itemsPerPage !== oldOptions.itemsPerPage
        const pageChanged = newOptions.page !== oldOptions.page
        if (pageChanged || itemPerPageChanged) {
          this.requestPageSettings({
            page: newOptions.page,
            itemsPerPage: newOptions.itemsPerPage
          })
        }
      },
      deep: true
    },
    statusQueryParam() {
      this.$refs.studentFilter.reset()
      this.loadDataOnCreated()
    },
    typeQueryParam() {
      this.$refs.studentFilter.reset()
      this.loadDataOnCreated()
    }
  },
  filters: {
    getStatus(status) {
      if (status === 'reserved') return 'Bảo Lưu'
      if (status === 'active') return 'Hoạt Động'
      if (status === 'deactive') return 'Thôi học'
      return status
    },
    getClassCount(classes) {
      return classes ? classes.length : 0
    }
  }
}
</script>
